import React, { useEffect, useState, useRef } from "react";
import { Form, Container, Row, Col, Card } from "react-bootstrap";
import { useDispatch } from "react-redux";
import {
  addDayFerry,
  editDayFerry,
  getDayPlan,
  getoptions, deleteDayFerry
} from "../../store/slices/package";
import { getFerry } from "../../store/slices/ferry";
import { toast } from "react-toastify";
import { getPlace } from "../../store/slices/place";

export default function FerryStep({
  packageId,
  ferries,
  setFerries,
  nextStep,
  prevStep,
}) {
  const dispatch = useDispatch();

  const [dayPlans, setDayPlans] = useState([]);
  const [options, setOptions] = useState([]);
  const [errors, setErrors] = useState([]);
  const [masterFerries, setMasterFerries] = useState({});
  const [places, setPlaces] = useState([]);
  const loadedFerryKeys = useRef(new Set());
  const isLoadingExisting = useRef(false);
  useEffect(() => {
    if (!packageId) return;

    const fetchData = async () => {
      try {
        const item = { packageId };

        const dpRes = await dispatch(getDayPlan(item)).unwrap();
        const optRes = await dispatch(getoptions(item)).unwrap();
        const placeRes = await dispatch(getPlace()).unwrap();

        setDayPlans(dpRes?.data || []);
        setOptions(optRes?.data || []);
        setPlaces(placeRes?.places || placeRes?.data || []);
      } catch (err) {
        toast.error("Failed to load ferry data");
      }
    };

    fetchData();
  }, [packageId, dispatch]);

  useEffect(() => {
    if (!options.length || !dayPlans.length) return;

    setFerries((prevFerries) => {
      const validOptionIds = options.map((o) => o._id);
      let filtered = prevFerries.filter(
        (f) => validOptionIds.includes(f.packageOption) && f.dayPlan,
      );

      // Ensure every option + day combination exists
      const ferryDays = dayPlans.slice(0, dayPlans.length - 1);

      options.forEach((option) => {
        ferryDays.forEach((day) => {
          const exists = filtered.find(
            (f) =>
              String(f.packageOption) === String(option._id) &&
              String(f.dayPlan) === String(day._id),
          );

          if (!exists) {
            filtered.push({
              _id: null,
              packageOption: option._id,
              dayPlan: day._id,
              ferries: [],
            });
          }
        });
      });

      filtered.sort((a, b) => {
        const dayA = dayPlans.find((d) => d._id === a.dayPlan)?.dayNumber || 0;
        const dayB = dayPlans.find((d) => d._id === b.dayPlan)?.dayNumber || 0;

        return dayA - dayB;
      });

      return filtered;
    });
  }, [options, dayPlans]);

  const addFerryRow = (configIndex) => {
    const updated = [...ferries];

    updated[configIndex].ferries.push({
      fromPlace: "",
      toPlace: "",
      ferry: "",
      ferryId: "",
      scheduleIndex: "",
      className: "",
      quantity: 1,
    });

    setFerries(updated);
  };

  const removeFerryRow = async (configIndex, ferryIndex) => {
    try {
      const updated = [...ferries];
      const config = updated[configIndex];
      const row = config.ferries[ferryIndex];

      // Remove from UI
      config.ferries.splice(ferryIndex, 1);
      setFerries(updated);

      // Existing configuration
      if (config._id) {
        // If no ferries left, delete entire config
        if (config.ferries.length === 0) {
          await dispatch(deleteDayFerry(config._id)).unwrap();

          setFerries((prev) =>
            prev.map((item, index) =>
              index === configIndex
                ? { ...item, _id: null, ferries: [] }
                : item
            )
          );
        } else {
          // Update remaining ferries
          const ferryPayload = config.ferries.map((f, idx) => {
            const ferryKey = `${configIndex}_${idx}`;
            const ferryData =
              (masterFerries[ferryKey] || [])[Number(f.ferry)];

            return {
              ferry: ferryData?.ferryId,
              fromPlace: f.fromPlace,
              toPlace: f.toPlace,
              scheduleIndex: Number(f.scheduleIndex),
              className: f.className,
              quantity: f.quantity || 1,
              costType: "perPerson",
            };
          });

          await dispatch(
            editDayFerry({
              id: config._id,
              ferries: ferryPayload,
            })
          ).unwrap();
        }
      }

      toast.success("Ferry removed");
    } catch (err) {
      toast.error("Failed to remove ferry");
      console.error(err);
    }
  };

  const handleChange = async (configIndex, ferryIndex, field, value) => {
    const updated = [...ferries];

    const row = {
      ...updated[configIndex].ferries[ferryIndex],
      [field]: value,
    };

    // Reset when source/destination changes
    if (
      field === "fromPlace" ||
      field === "toPlace"
    ) {
      row.ferry = "";
      row.ferryId = "";
      row.scheduleIndex = "";
      row.className = "";
    }

    // Reset when ferry changes
    if (field === "ferry") {
      row.scheduleIndex = "";
      row.className = "";
    }

    // Reset when schedule changes
    if (field === "scheduleIndex") {
      row.className = "";
    }

    updated[configIndex].ferries[ferryIndex] = row;

    const key = `${configIndex}_${ferryIndex}`;
    const from = row.fromPlace;
    const to = row.toPlace;

    // existing logic (unchanged)
    if ((field === "fromPlace" || field === "toPlace") && from && to) {
      try {
        const ferryRes = await dispatch(
          getFerry({ from: from, to: to }),
        ).unwrap();

        const route = ferryRes?.ferryData?.[0];

        const ferriesList =
          route?.ferries?.map((f, idx) => ({
            id: idx,
            routeId: route._id,
            ferryId: f._id,
            key: `${route._id}_${idx}`,
            name: f.name,
            vendor: f.vendor,
            schedules: f.schedules,
          })) || [];

        setMasterFerries((prev) => ({
          ...prev,
          [key]: ferriesList,
        }));
      } catch {
        toast.error("Failed to load ferries");
      }
    }

    setFerries(updated);
  };

  useEffect(() => {
    if (!ferries?.length) return;

    if (isLoadingExisting.current) return;

    const existingRows = [];

    ferries.forEach((config, configIndex) => {
      config.ferries?.forEach((row, ferryIndex) => {
        if (
          row.ferryId &&
          row.fromPlace &&
          row.toPlace
        ) {
          const key =
            `${config._id || "new"}_${row._id || row.ferryId}_${configIndex}_${ferryIndex}`;

          existingRows.push({
            row,
            configIndex,
            ferryIndex,
            key,
          });
        }
      });
    });

    if (!existingRows.length) return;

    const rowsToLoad = existingRows.filter(
      (item) => !loadedFerryKeys.current.has(item.key)
    );

    if (!rowsToLoad.length) return;

    isLoadingExisting.current = true;

    const loadExistingFerries = async () => {
      const newMasterFerries = {};
      const ferryIndexUpdates = {};

      try {
        for (const item of rowsToLoad) {
          const {
            row,
            configIndex,
            ferryIndex,
            key,
          } = item;

          const ferryRes = await dispatch(
            getFerry({
              from: row.fromPlace,
              to: row.toPlace,
            })
          ).unwrap();

          const route = ferryRes?.ferryData?.[0];

          const ferriesList =
            route?.ferries?.map((f, index) => ({
              id: index,
              routeId: route._id,
              ferryId: f._id,
              key: `${route._id}_${index}`,
              name: f.name,
              vendor: f.vendor,
              schedules: f.schedules || [],
            })) || [];

          // IMPORTANT:
          // UI still uses configIndex_ferryIndex
          const uiKey =
            `${configIndex}_${ferryIndex}`;

          newMasterFerries[uiKey] =
            ferriesList;

          const selectedIndex =
            ferriesList.findIndex(
              (f) =>
                String(f.ferryId) ===
                String(row.ferryId)
            );

          if (selectedIndex >= 0) {
            ferryIndexUpdates[uiKey] =
              selectedIndex;
          }

          loadedFerryKeys.current.add(key);
        }

        if (Object.keys(newMasterFerries).length) {
          setMasterFerries((prev) => ({
            ...prev,
            ...newMasterFerries,
          }));
        }

        if (Object.keys(ferryIndexUpdates).length) {
          setFerries((prev) =>
            prev.map((config, configIndex) => ({
              ...config,

              ferries: config.ferries?.map(
                (row, ferryIndex) => {
                  const uiKey =
                    `${configIndex}_${ferryIndex}`;

                  if (
                    ferryIndexUpdates[uiKey] !==
                    undefined
                  ) {
                    return {
                      ...row,
                      ferry:
                        ferryIndexUpdates[uiKey],
                    };
                  }

                  return row;
                }
              ),
            }))
          );
        }
      } catch (error) {
        console.error(
          "Failed to load existing ferries:",
          error
        );
      } finally {
        isLoadingExisting.current = false;
      }
    };

    loadExistingFerries();
  }, [ferries, dispatch]);



  const validateFerryForm = () => {
    let isValid = true;
    const newErrors = [];

    ferries.forEach((config, i) => {
      newErrors[i] = {};

      config.ferries?.forEach((row, j) => {
        if (row.ferry === "" || row.ferry === undefined) return;

        if (row.scheduleIndex === "" || row.scheduleIndex === undefined) {
          isValid = false;
        }

        if (!row.className) {
          isValid = false;
        }
      });
    });

    setErrors(newErrors);

    return isValid;
  };

  const copyToAllOptions = (sourceConfig, sourceIndex) => {
    const updatedMasterFerries = { ...masterFerries };

    setFerries((prev) => {
      return prev.map((config, configIndex) => {

        // same day, different option
        if (
          String(config.dayPlan) ===
          String(sourceConfig.dayPlan) &&
          String(config.packageOption) !==
          String(sourceConfig.packageOption)
        ) {

          const copiedFerries = sourceConfig.ferries.map(
            (ferryRow, rowIndex) => {

              // copy master ferry cache also
              const sourceKey = `${sourceIndex}_${rowIndex}`;
              const targetKey = `${configIndex}_${rowIndex}`;

              if (masterFerries[sourceKey]) {
                updatedMasterFerries[targetKey] =
                  masterFerries[sourceKey];
              }

              return {
                ...ferryRow,
              };
            },
          );

          return {
            ...config,
            ferries: copiedFerries,
          };
        }

        return config;
      });
    });

    setMasterFerries(updatedMasterFerries);

    toast.success("Copied to all options");
  };
  const handleSubmit = async () => {
    if (!validateFerryForm()) return;

    try {
      const grouped = {};

      for (let i = 0; i < ferries.length; i++) {
        const config = ferries[i];

        if (!config.ferries || config.ferries.length === 0) continue;

        for (let j = 0; j < config.ferries.length; j++) {
          const row = config.ferries[j];

          if (row.ferry === "" || row.ferry === undefined) continue;

          const ferryKey = `${i}_${j}`;
          const ferriesList = masterFerries[ferryKey] || [];
          const ferryIndex = Number(row.ferry);
          const ferryData = ferriesList[ferryIndex];

          if (!ferryData) {
            console.warn(
              "Skipping ferry row because ferry list not loaded",
              row,
            );
            continue;
          }

          const groupKey = `${config.dayPlan}_${config.packageOption}`;

          if (!grouped[groupKey]) {
            grouped[groupKey] = {
              id: config._id || null,
              dayPlan: config.dayPlan,
              packageOption: config.packageOption,
              ferries: [],
            };
          }

          grouped[groupKey].ferries.push({
            ferry: ferryData.ferryId,
            fromPlace: row.fromPlace,
            toPlace: row.toPlace,
            scheduleIndex: Number(row.scheduleIndex),
            className: row.className,
            quantity: row.quantity || 1,
            costType: "perPerson",
          });
        }
      }

      const payloads = Object.values(grouped);

      if (payloads.length === 0) {
        toast.warning("No ferry selected");
        nextStep();
        return;
      }

      for (const payload of payloads) {
        if (payload.id) {
          await dispatch(
            editDayFerry({
              id: payload.id,
              ferries: payload.ferries,
            }),
          ).unwrap();
        } else {
          const res = await dispatch(addDayFerry(payload)).unwrap();

          const newId = res?._id || res?.data?._id;

          ferries.forEach((f) => {
            if (
              f.dayPlan === payload.dayPlan &&
              f.packageOption === payload.packageOption
            ) {
              f._id = newId;
            }
          });
        }
      }

      toast.success("Ferry configurations saved");
      nextStep();
    } catch (err) {
      console.error(err);
      toast.error("Failed to save ferries");
    }
  };

  const ferriesByOption = options.map((option) => ({
    option,
    configs: ferries.filter((f) => f.packageOption === option._id),
  }));

  return (
    <Container>
      <Card className="p-4 shadow-sm border-0 mt-2">
        <Row>
          {ferriesByOption.map(({ option, configs }) => (
            <Col md={6} key={option._id}>
              <Card className="p-3 mb-3 shadow-sm">
                <h5 className="text-center mb-3">{option.name}</h5>

                {configs.map((config) => {
                  const index = ferries.findIndex(
                    (f) =>
                      f.packageOption === config.packageOption &&
                      f.dayPlan === config.dayPlan,
                  );

                  const day = dayPlans.find((d) => d._id === config.dayPlan);

                  return (
                    <Card key={index} className="mb-3 p-3 border">
                      <h6>Day {day?.dayNumber}</h6>

                      {config.ferries.map((row, ferryIndex) => {
                        const ferryKey = `${index}_${ferryIndex}`;
                        const selectedFerry =
                          row.ferry !== "" &&
                            masterFerries[ferryKey]
                            ? masterFerries[ferryKey][Number(row.ferry)]
                            : null;

                        return (
                          <Row key={ferryIndex} className="g-3 mb-2">
                            <Col md={6}>
                              <Form.Label>From</Form.Label>
                              <Form.Select
                                value={row.fromPlace}
                                onChange={(e) =>
                                  handleChange(
                                    index,
                                    ferryIndex,
                                    "fromPlace",
                                    e.target.value,
                                  )
                                }
                              >
                                <option value="">Select</option>
                                {places
                                  .filter((p) => p._id !== row.toPlace)
                                  .map((p) => (
                                    <option key={p._id} value={p._id}>
                                      {p.name}
                                    </option>
                                  ))}
                              </Form.Select>
                            </Col>

                            {/* To */}
                            <Col md={6}>
                              <Form.Label>To</Form.Label>
                              <Form.Select
                                value={row.toPlace}
                                onChange={(e) =>
                                  handleChange(
                                    index,
                                    ferryIndex,
                                    "toPlace",
                                    e.target.value,
                                  )
                                }
                              >
                                <option value="">Select</option>
                                {places
                                  .filter((p) => p._id !== row.fromPlace)
                                  .map((p) => (
                                    <option key={p._id} value={p._id}>
                                      {p.name}
                                    </option>
                                  ))}
                              </Form.Select>
                            </Col>

                            <Col md={6}>
                              <Form.Label>Ferry</Form.Label>
                              <Form.Select
                                value={row.ferry}
                                disabled={!row.fromPlace || !row.toPlace}
                                onChange={(e) =>
                                  handleChange(
                                    index,
                                    ferryIndex,
                                    "ferry",
                                    e.target.value,
                                  )
                                }
                              >
                                <option value="">Select Ferry</option>

                                {(masterFerries[ferryKey] || []).map((f) => (
                                  <option key={f.key} value={f.id}>
                                    {f.name}
                                  </option>
                                ))}
                              </Form.Select>
                            </Col>

                            {selectedFerry && (
                              <Col md={6}>
                                <Form.Label>Schedule</Form.Label>
                                <Form.Select
                                  value={row.scheduleIndex}
                                  onChange={(e) =>
                                    handleChange(
                                      index,
                                      ferryIndex,
                                      "scheduleIndex",
                                      Number(e.target.value),
                                    )
                                  }
                                >
                                  <option value="">Select Schedule</option>

                                  {selectedFerry.schedules.map((sch, i) => (
                                    <option key={i} value={i}>
                                      {sch.departureTime} → {sch.arrivalTime}
                                    </option>
                                  ))}
                                </Form.Select>
                              </Col>
                            )}

                            {/* Class */}
                            {selectedFerry &&
                              row.scheduleIndex !== undefined &&
                              row.scheduleIndex !== null &&
                              selectedFerry.schedules?.[Number(row.scheduleIndex)] && (
                                <Col md={6}>
                                  <Form.Label>Class</Form.Label>
                                  <Form.Select
                                    value={row.className}
                                    onChange={(e) =>
                                      handleChange(
                                        index,
                                        ferryIndex,
                                        "className",
                                        e.target.value,
                                      )
                                    }
                                  >
                                    <option value="">Select Class</option>

                                    {selectedFerry.schedules[
                                      Number(row.scheduleIndex)
                                    ]?.classes?.map((cls, i) => (
                                      <option key={i} value={cls.className}>
                                        {cls.className} (₹{cls.price})
                                      </option>
                                    ))}
                                  </Form.Select>
                                </Col>
                              )}

                            <Col md={6}>
                              <button
                                type="button"
                                className="btn btn-danger btn-sm mt-4"
                                onClick={() =>
                                  removeFerryRow(index, ferryIndex)
                                }
                              >
                                ✕
                              </button>
                            </Col>
                          </Row>
                        );
                      })}

                      <div className="d-flex gap-2 mt-2">
                        <button
                          type="button"
                          className="cancel-btn btn-sm"
                          onClick={() => addFerryRow(index)}
                        >
                          + Add Ferry
                        </button>

                        <button
                          type="button"
                          className="cancel-btn btn-sm"
                          onClick={() => copyToAllOptions(config, index)}
                        >
                          Copy To All
                        </button>
                      </div>
                    </Card>
                  );
                })}
              </Card>
            </Col>
          ))}
        </Row>

        <div className="d-flex justify-content-between mt-4 mb-5">
          <button className="cancel-btn" onClick={prevStep}>
            Previous
          </button>

          <button className="submit-btn" onClick={handleSubmit}>
            Next
          </button>
        </div>
      </Card>
    </Container>
  );
}